// Pop Market in-website direct chat. Opened from the "💬 CHAT" button
// on forum orders — the clicker + the order poster get a private,
// deterministic thread they can DM in without leaving the site.
//
// Storage:
//   pop_market_chats/{threadId}                       — thread metadata
//   pop_market_chats/{threadId}/messages/{msgId}      — chat messages
//
// Thread ID is a sorted join of the two UIDs so both parties always
// resolve to the same doc regardless of who opens it first.

import {
  collection, doc, setDoc, addDoc,
  query, orderBy, limit, onSnapshot, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const CHATS_COL = "pop_market_chats";
export const CHAT_MSG_MAX = 1000;
export const CHAT_TAIL_LIMIT = 200;

export function threadIdFor(uidA, uidB) {
  if (!uidA || !uidB) throw new Error("threadIdFor: missing uid");
  return [String(uidA), String(uidB)].sort().join("__");
}

// Ensure the thread doc exists — safe to call every time the drawer
// opens. Uses setDoc merge:true rather than a read-then-write: reading
// a nonexistent doc under our participants-only rule returns
// PERMISSION_DENIED (Firestore refuses to leak existence when the read
// rule depends on `resource.data`). Since the thread ID is
// deterministic from the participant pair, the participants array can
// never differ between callers, so merge:true is safe both as first-
// create and repeat-update.
export async function ensureThread({
  myUid,
  otherUid,
  myUsername,
  otherUsername,
}) {
  const threadId = threadIdFor(myUid, otherUid);
  const ref = doc(db, CHATS_COL, threadId);
  const participants = [String(myUid), String(otherUid)].sort();
  await setDoc(ref, {
    participants,
    participantUsernames: {
      [myUid]: myUsername || null,
      [otherUid]: otherUsername || null,
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return threadId;
}

export async function sendMessage(threadId, myUid, rawText) {
  const text = String(rawText || "").trim().slice(0, CHAT_MSG_MAX);
  if (!text) return;
  await addDoc(collection(db, CHATS_COL, threadId, "messages"), {
    from: myUid,
    text,
    createdAt: serverTimestamp(),
  });
  // Best-effort — the message write already succeeded even if this
  // metadata update fails (rules would only reject if the writer
  // isn't a participant, which we already validated by writing above).
  try {
    await setDoc(
        doc(db, CHATS_COL, threadId),
        {
          lastMessage: {
            text: text.slice(0, 120),
            from: myUid,
            at: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
    );
  } catch (_) { /* ignore */ }
}

export function subscribeMessages(threadId, cb) {
  if (!threadId) { cb([]); return () => {}; }
  const q = query(
      collection(db, CHATS_COL, threadId, "messages"),
      orderBy("createdAt", "asc"),
      limit(CHAT_TAIL_LIMIT),
  );
  return onSnapshot(q,
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn("[pop-market] chat subscribeMessages failed", err);
        cb([]);
      },
  );
}
